import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Search, ShieldAlert } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  createImpersonationChallenge,
  getImpersonationStatus,
  searchImpersonationUsers,
  startImpersonation,
} from '@services/superAdminImpersonation';
import useSession from '@hooks/useSession';
import { getAccessToken, setAccessToken } from '../../utils/authTokenStore';

const ACTOR_TOKEN_KEY = 'superAdminActorToken';
const IMPERSONATION_CONTEXT_KEY = 'superAdminImpersonationContext';

const getErrorMessage = (error, fallback) => {
  if (typeof error === 'string') {
    return error;
  }
  return error?.detail || error?.message || fallback;
};

export default function SuperAdminImpersonationDialog({ open, onClose }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { setIsAuthenticated } = useSession();
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reason, setReason] = useState('');
  const [emailConfirmation, setEmailConfirmation] = useState('');
  const [actorPassword, setActorPassword] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [startLoading, setStartLoading] = useState(false);
  const [error, setError] = useState('');

  const actorToken = useMemo(() => getAccessToken(), [open]);
  const normalizedTargetEmail = (selectedUser?.email || '').trim().toLowerCase();
  const normalizedEmailConfirmation = emailConfirmation.trim().toLowerCase();
  const reasonReady = reason.trim().length >= 8;
  const emailReady =
    Boolean(normalizedTargetEmail) && normalizedEmailConfirmation === normalizedTargetEmail;
  const passwordReady = actorPassword.length > 0;
  let startDisabledReason = '';
  if (selectedUser && !reason.trim()) {
    startDisabledReason = 'Enter a support reason.';
  } else if (selectedUser && !reasonReady) {
    startDisabledReason = 'Support reason must be at least 8 characters.';
  } else if (selectedUser && !emailConfirmation.trim()) {
    startDisabledReason = `Type ${normalizedTargetEmail} to confirm the target account.`;
  } else if (selectedUser && !emailReady) {
    startDisabledReason = 'Target email confirmation does not match the selected user.';
  } else if (selectedUser && !passwordReady) {
    startDisabledReason = 'Enter your Qualz password to confirm this support action.';
  }
  const canStart = Boolean(selectedUser && !startDisabledReason);

  useEffect(() => {
    if (!open) {
      return;
    }

    setStatus(null);
    setUsers([]);
    setSelectedUser(null);
    setReason('');
    setEmailConfirmation('');
    setActorPassword('');
    setError('');

    if (!getAccessToken()) {
      return;
    }

    setStatusLoading(true);
    getImpersonationStatus()
      .then((response) => setStatus(response))
      .catch((err) => setError(getErrorMessage(err, 'Unable to verify admin session')))
      .finally(() => setStatusLoading(false));
  }, [open]);

  const handleSearch = async () => {
    if (query.trim().length < 3) {
      setError('Enter at least 3 characters to search.');
      return;
    }
    setError('');
    setSearchLoading(true);
    setSelectedUser(null);
    try {
      const response = await searchImpersonationUsers({ query: query.trim() });
      setUsers(response.users || []);
    } catch (err) {
      setUsers([]);
      setError(getErrorMessage(err, 'Unable to search users'));
    } finally {
      setSearchLoading(false);
    }
  };

  const clearWorkspaceState = () => {
    localStorage.removeItem('activeWorkspaceId');
    localStorage.removeItem('previousActiveWorkspaceId');
    queryClient.clear();
  };

  const handleStart = async () => {
    if (!canStart) {
      return;
    }
    setError('');
    setStartLoading(true);
    try {
      const challenge = await createImpersonationChallenge({
        targetEmail: normalizedTargetEmail,
        reason: reason.trim(),
      });
      const response = await startImpersonation({
        challengeId: challenge.challenge_id,
        targetEmailConfirmation: normalizedEmailConfirmation,
        reason: reason.trim(),
        actorPassword,
      });
      localStorage.setItem(ACTOR_TOKEN_KEY, actorToken);
      setAccessToken(response.access_token);
      localStorage.setItem(IMPERSONATION_CONTEXT_KEY, JSON.stringify(response.impersonation || {}));
      clearWorkspaceState();
      setIsAuthenticated(true);
      toast.warn(`Impersonating ${response.impersonation?.target_email || normalizedTargetEmail}`);
      onClose();
      navigate('/');
    } catch (err) {
      setError(getErrorMessage(err, 'Unable to start impersonation'));
    } finally {
      setStartLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" scroll="paper">
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.25,
          px: 3,
          py: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: '#fff',
          flexShrink: 0,
          zIndex: 1,
        }}
      >
        <ShieldAlert size={22} color="#b42318" />
        <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
          Support Login-As
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          px: 3,
          py: '20px !important',
        }}
      >
        <Stack spacing={2}>
          {!actorToken && (
            <Alert severity="info">
              Sign in with an allowlisted Qualz account before opening a login-as session.
            </Alert>
          )}

          {actorToken && statusLoading && (
            <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}>
              <CircularProgress size={24} />
            </Box>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {status && !status.enabled && (
            <Alert severity="warning">Login-as is disabled for this environment.</Alert>
          )}

          {status && status.enabled && !status.allowed && (
            <Alert severity="warning">This account is not allowlisted for login-as.</Alert>
          )}

          {status?.enabled && status?.allowed && (
            <>
              <TextField
                label="Find customer member"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    handleSearch();
                  }
                }}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                variant="outlined"
                onClick={handleSearch}
                disabled={searchLoading || query.trim().length < 3}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                {searchLoading ? <CircularProgress size={18} /> : 'Search'}
              </Button>

              {users.length > 0 && (
                <List
                  dense
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    maxHeight: 220,
                    overflow: 'auto',
                  }}
                >
                  {users.map((user) => (
                    <ListItemButton
                      key={user.id}
                      selected={selectedUser?.id === user.id}
                      onClick={() => {
                        setSelectedUser(user);
                        setEmailConfirmation('');
                      }}
                    >
                      <ListItemText
                        primary={user.email}
                        secondary={[user.first_name, user.last_name, user.customer_name]
                          .filter(Boolean)
                          .join(' / ')}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}

              {selectedUser && (
                <>
                  <Divider />
                  <Box
                    sx={{
                      border: '1px solid',
                      borderColor: '#d0d5dd',
                      borderRadius: 1,
                      p: 2,
                      backgroundColor: '#f9fafb',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
                      {selectedUser.email}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {[selectedUser.first_name, selectedUser.last_name, selectedUser.customer_name]
                        .filter(Boolean)
                        .join(' / ')}
                    </Typography>
                  </Box>

                  <TextField
                    label="Support reason"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    multiline
                    minRows={2}
                    fullWidth
                    required
                    error={Boolean(reason) && !reasonReady}
                    helperText={
                      reasonReady ? 'Required for audit logs.' : 'Use at least 8 characters.'
                    }
                  />
                  <TextField
                    label="Confirm target email"
                    value={emailConfirmation}
                    onChange={(event) => setEmailConfirmation(event.target.value)}
                    fullWidth
                    required
                    error={Boolean(emailConfirmation) && !emailReady}
                    helperText={`Type ${normalizedTargetEmail} exactly.`}
                  />
                  <TextField
                    label="Confirm your password"
                    value={actorPassword}
                    onChange={(event) => setActorPassword(event.target.value)}
                    type="password"
                    fullWidth
                    required
                    helperText="Your current Qualz password is re-checked before starting."
                  />
                  <Alert severity="error">
                    This session is audited and uses the target user&apos;s workspace permissions.
                    Write actions are allowed as that user.
                  </Alert>
                </>
              )}
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {selectedUser && startDisabledReason && (
          <Typography variant="caption" sx={{ mr: 'auto', color: 'text.secondary' }}>
            To continue: {startDisabledReason}
          </Typography>
        )}
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cancel
        </Button>
        {status?.enabled && status?.allowed && (
          <Button
            variant="contained"
            color="error"
            onClick={handleStart}
            disabled={!canStart || startLoading}
            sx={{ textTransform: 'none' }}
          >
            {startLoading ? <CircularProgress size={18} color="inherit" /> : 'Start Login-As'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
